/// <amd-module name="Build/DesignerExtensions/FileSelectPanel/FileSelectPanel" />
import * as React from "react";

import { BranchDropdown, branchDropdownComboBoxInputSelector } from "Build/Common/Library/Legacy/Components/BranchDropdown";
import { openLinkSecurely } from "Build/Common/Library/Navigation";
import { ConfigFilesResult, ConfigFileType, IRepositoryService } from "Build/Common/Library/RepositoryService";
import { RepositoryDescriptor } from "Build/Common/Library/Types";
import * as Resources from "Build/Designer/Resources";
import { ConfigurationFileInfo, RepositoryAnalysisInfo, TemplateInfo, WizardState } from "Build/Designer/Wizard.types";

import * as StringUtil from "VSS/Core/Util/String";
import { Uri } from "VSS/Platform/Util/Url";

import { IVssContributionService } from "VSS/Platform/Contribution";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { IButtonProps } from "VSSUI/Button";
import { ContentSize } from "VSSUI/Callout";
import { ComboBox, IComboBoxOption } from "VSSUI/ComboBox";
import { FormItem } from "VSSUI/FormItem";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Panel } from "VSSUI/Panel";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

interface Props {
    wizardState: WizardState;
    isOpen: boolean;
    onDismiss: () => void;
    onTemplateSelected: (template?: TemplateInfo, repoAnalysis?: RepositoryAnalysisInfo, configuration?: ConfigurationFileInfo, branch?: string) => void;
}

interface State {
    configFiles: IComboBoxOption[];
    loadingFiles: boolean;
    branch: string;
    path: string;
    isValidFileFound?: boolean;
    configurationFile?: ConfigurationFileInfo;
    fileNotFoundErrorMessage: string;
    dataProviderErrorMessage: string;
}

export class FileSelectPanel extends VssComponent<Props, State, IVssComponentContext> {
    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);

        const state: State = {
            configFiles: [],
            loadingFiles: true,
            branch: this.props.wizardState.branch,
            path: "",
            isValidFileFound: undefined,
            configurationFile: undefined,
            fileNotFoundErrorMessage: "",
            dataProviderErrorMessage: "",
        }
        this.state = state;
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this._getConfigFiles(this.state.branch);
    }

    public render(): JSX.Element {
        const footerButtons: IButtonProps[] = [{
            onClick: this.props.onDismiss,
            text: Resources.Cancel
        },
        {
            className: "primary-action-button",
            disabled: !this.state.isValidFileFound,
            onClick: this._onContinueClick,
            primary: true,
            text: Resources.Continue
        }];

        return (
            <div>
                {this.props.isOpen &&
                    <Panel
                        ariaLabel={Resources.BringYourOwnYamlTitleShort}
                        size={ContentSize.Medium}
                        onDismiss={this.props.onDismiss}
                        titleProps={{ text: Resources.BringYourOwnYamlTitleShort }}
                        description={Resources.BringYourOwnYamlDesc}
                        footerButtonProps={footerButtons}
                        defaultActiveElement={branchDropdownComboBoxInputSelector}>
                        {this._renderBody()}
                    </Panel>
                }
            </div>
        );
    }

    private _renderBody(): JSX.Element {
        return (
            <div className="ci-designer-file-select-panel-body flex-column flex-grow">
                {
                    this.state.dataProviderErrorMessage !== "" &&
                    <MessageCard className="error" severity={MessageCardSeverity.Error}>
                        {this.state.dataProviderErrorMessage}
                    </MessageCard>
                }
                <BranchDropdown
                    className="branch"
                    label={Resources.Branch}
                    repositoryType={this.props.wizardState.sourceProvider}
                    repositoryId={this.props.wizardState.repositoryId}
                    defaultBranch={this.state.branch}
                    connectionId={this.props.wizardState.connectionId}
                    onBranchSelected={this._setBranch}
                    onError={this._setDataProviderError}
                    isDrodownFullWidth={true} />
                <FormItem
                    className="path"
                    label={Resources.Path}
                    message={Resources.SelectYamlDescription}>
                    <ComboBox
                        options={this.state.configFiles}
                        autoComplete={true}
                        allowFreeform={true}
                        errorMessage={this.state.fileNotFoundErrorMessage}
                        onChanged={this._setPath}
                        onRenderOption={this._onRenderOption}
                        onResolveOptions={this._getOptions}
                        text={this.state.path} />
                </FormItem>
                {
                    this._isRepositoryUrlValid() &&
                    <Link
                        className="repo-link flex-row"
                        href={this.props.wizardState.repositoryUrl}
                        onClick={this._onRepositoryClicked}>
                        {this.props.wizardState.repositoryName}
                        <Icon className="external-link-icon" iconName="NavigateExternalInline" />
                    </Link>
                }
            </div>
        );
    }

    private _onContinueClick = () => {
        this.props.onTemplateSelected(undefined, undefined, this.state.configurationFile, this.state.branch);
    }

    private _onRenderOption = (item: IComboBoxOption): JSX.Element => {
        if (this.state.loadingFiles) {
            return <Spinner size={SpinnerSize.medium} label={Resources.LoadingYAMLFiles}/>;
        }
        else {
            return <span>{item.text}</span>;
        }
    }

    private _getConfigFiles(branch: string) {
        const repositoryService = this.context.pageContext.getService<IRepositoryService>("IRepositoryService");
        const repository: RepositoryDescriptor = {
            connectionId: this.props.wizardState.connectionId,
            sourceProviderType: this.props.wizardState.sourceProvider,
            id: this.props.wizardState.repositoryId,
            name: this.props.wizardState.repositoryName,
            projectId: this.props.wizardState.repositoryProjectId,
            defaultBranch: branch,
        };

        repositoryService.getConfigFiles(repository, ConfigFileType.Yaml)
            .then((configFilesResult: ConfigFilesResult) => {
                const comboBoxOptions = configFilesResult.files
                    .map((filePath) => {
                        return {
                            key: filePath,
                            text: filePath
                        };
                    }) as IComboBoxOption[];

                this.setState({
                    dataProviderErrorMessage: "",
                    configFiles: comboBoxOptions,
                    loadingFiles: false
                });
            }, (error: Error) => {
                const message = (error && error.message) || StringUtil.format(Resources.ConfigFileDataProviderErrorMessage, this.props.wizardState.repositoryName || this.props.wizardState.repositoryId);
                this.setState({
                    dataProviderErrorMessage: message,
                    loadingFiles: false
                });
            });
    }

    private _getOptions = (): IComboBoxOption[] => {
        if (this.state.loadingFiles) {
            return [
                { key: "loading", text: "", disabled: true }
            ];
        }

        return this.state.configFiles;
    }

    private _setDataProviderError = (error: string) => {
        this.setState({ dataProviderErrorMessage: error });
    }

    // if an option is selected from the dropdown, the branch name will be in that option
    // if a user types in a branch, the branch name will be in value
    private _setBranch = (branch: string) => {
        if(branch && this.state.branch != branch) {
            this.setState({ branch, configFiles: [], isValidFileFound: false, loadingFiles: true }, () => {
                this._validateFile(this.state.path);
            });
            this._getConfigFiles(branch);
        }
    }

    private _setPath = (option?: IComboBoxOption, index?: number, value?: string) => {
        const path = option ? option.text : value;
        if (path && this.state.path != path) {
            this.setState({ path, isValidFileFound: false });
            this._validateFile(path);
        }
    }

    private _validateFile(path: string) {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");

        const params = { ...this.props.wizardState, branch: this.state.branch, path };
        contributionService.getDataAsync<ConfigurationFileInfo>("ms.vss-build-web.existing-configuration-data-provider", params)
            .then((configuration: ConfigurationFileInfo) => {
                if (configuration) {
                    this.setState({
                        isValidFileFound: true,
                        configurationFile: configuration,
                        fileNotFoundErrorMessage: "",
                    });
                }
                else {
                    this.setState({
                        isValidFileFound: false,
                        fileNotFoundErrorMessage: Resources.ConfigFileNotFound,
                    });
                }
            }, (error: Error) => {
                const message = (error && error.message) || Resources.ConfigFileNotFound;
                this.setState({
                    isValidFileFound: false,
                    fileNotFoundErrorMessage: message,
                });
            });
    }

    // repositoryUrl is specified as absolute with http/https scheme or as relative
    private _isRepositoryUrlValid() {
        if (this.props.wizardState.repositoryUrl) {
            try {
                const url = new Uri(this.props.wizardState.repositoryUrl, { absoluteUriRequired: true });
                if (url.absoluteUri && (StringUtil.equals(url.scheme, "http", true) || StringUtil.equals(url.scheme, "https", true))) {
                    return true;
                }
            }
            catch (e) {
                return true;
            }
        }
        return false;
    }

    private _onRepositoryClicked = (event: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>) => {
        event.preventDefault(); // Stops the link href from being followed
        if (this.props.wizardState.repositoryUrl) {
            openLinkSecurely(this.props.wizardState.repositoryUrl, true);
        }
    }
}

Components.add("designer-file-select-panel", FileSelectPanel);
